import React, { useState, useEffect } from "react";
import {
  Alert,
  Grid,
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  MenuItem,
  Container,
} from "@mui/material";
import RequiredLabel from "../../shared/RequiredLabel";
import { formatJpDateTime } from "../../utils/dateUtils";
import PrefectureSelect from "../../shared/PrefectureSelect";

const JobRequestEdit = ({ userJobRequest, csrfToken, formAction }) => {
  const [errors, setErrors] = useState([]);
  const [validationTriggered, setValidationTriggered] = useState(false);

  // フォームの状態
  const [formState, setFormState] = useState({
    desired_joining_date: userJobRequest?.desired_joining_date || "",
    expected_retirement_date: userJobRequest?.expected_retirement_date || "",
    expected_salary_min: userJobRequest?.expected_salary_min || "",
    expected_salary_max: userJobRequest?.expected_salary_max || "",
    prefecture_ids: userJobRequest?.prefecture_ids || [],
    desired_employment_type:
      userJobRequest?.desired_employment_type ?? "full_time",
    remark: userJobRequest?.remark || "",
    current_salary: userJobRequest?.current_salary || "",
    career_change_count: userJobRequest?.career_change_count || "",
    previous_companies_and_reasons:
      userJobRequest?.previous_companies_and_reasons || "",
    current_change_reason: userJobRequest?.current_change_reason || "",
  });

  // エラーが出たらページ上部にスクロール
  useEffect(() => {
    if (validationTriggered && errors.length > 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [validationTriggered]);

  // 指定された日付が過去かを判定（空文字は除外）
  const isDateInPast = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(dateStr) < today;
  };

  // 退社予定日が入社希望日以降になっていないか
  const isRetirementBeforeJoining = () => {
    if (!formState.desired_joining_date || !formState.expected_retirement_date)
      return false;
    const join = new Date(formState.desired_joining_date);
    const retire = new Date(formState.expected_retirement_date);
    return retire >= join;
  };

  // 年収下限が年収上限より大きいか
  const isSalaryMinGreaterThanMax = () => {
    if (!formState.expected_salary_min || !formState.expected_salary_max)
      return false;
    const min = Number(formState.expected_salary_min);
    const max = Number(formState.expected_salary_max);
    return min >= max;
  };

  // 数値が空または負の値か
  const isEmptyOrNegative = (value) => {
    const num = Number(value);
    return value === "" || isNaN(num) || num < 0;
  };

  // 空文字・null・undefined を空とみなす
  const isEmpty = (value) =>
    value === "" || value === null || value === undefined;

  // フィールド別のエラーフラグ（赤枠表示用）
  // 日付
  const isInvalidJoiningDate =
    validationTriggered && isDateInPast(formState.desired_joining_date);
  const isInvalidRetirementDate =
    validationTriggered &&
    (isDateInPast(formState.expected_retirement_date) ||
      isRetirementBeforeJoining());

  // 年収（数値チェック）
  const isInvalidSalaryMin =
    validationTriggered && Number(formState.expected_salary_min) < 0;
  const isInvalidSalaryMax =
    validationTriggered &&
    (Number(formState.expected_salary_max) < 0 || isSalaryMinGreaterThanMax());

  const isInvalidCurrentSalary =
    validationTriggered && isEmptyOrNegative(formState.current_salary);
  const isInvalidChangeCount =
    validationTriggered && isEmptyOrNegative(formState.career_change_count);
  const isInvalidPrefecture =
    validationTriggered && formState.prefecture_ids.length === 0;
  const isInvalidEmploymentType =
    validationTriggered && isEmpty(formState.desired_employment_type);
  const isInvalidPreviousReasons =
    validationTriggered && isEmpty(formState.previous_companies_and_reasons);
  const isInvalidCurrentReason =
    validationTriggered && isEmpty(formState.current_change_reason);

  // バリデーション
  const validateForm = () => {
    const newErrors = [];

    const rules = [
      {
        field: "desired_joining_date",
        isError:
          !isEmpty(formState.desired_joining_date) &&
          isDateInPast(formState.desired_joining_date),
        message: "入社希望日は今日以降の日付を指定してください。",
      },
      {
        field: "expected_retirement_date",
        isError:
          !isEmpty(formState.expected_retirement_date) &&
          isDateInPast(formState.expected_retirement_date),
        message: "退社予定日は今日以降の日付を指定してください。",
      },
      {
        field: "expected_retirement_date",
        isError:
          !isEmpty(formState.desired_joining_date) &&
          !isEmpty(formState.expected_retirement_date) &&
          isRetirementBeforeJoining(),
        message: "退社予定日は入社希望日より前に設定してください。",
      },
      {
        field: "expected_salary_min",
        isError:
          !isEmpty(formState.expected_salary_min) &&
          Number(formState.expected_salary_min) < 0,
        message: "希望年収（下限）は0以上の数値で入力してください。",
      },
      {
        field: "expected_salary_max",
        isError:
          !isEmpty(formState.expected_salary_max) &&
          Number(formState.expected_salary_max) < 0,
        message: "希望年収（上限）は0以上の数値で入力してください。",
      },
      {
        field: "expected_salary_max",
        isError:
          !isEmpty(formState.expected_salary_min) &&
          !isEmpty(formState.expected_salary_max) &&
          isSalaryMinGreaterThanMax(),
        message: "希望年収の上限は下限以上にしてください。",
      },
      {
        field: "prefecture_ids",
        isError: formState.prefecture_ids.length === 0,
        message: "希望勤務地は入力必須です。",
      },
      {
        field: "desired_employment_type",
        isError: isEmpty(formState.desired_employment_type),
        message: "希望雇用形態は入力必須です。",
      },
      {
        field: "current_salary",
        isError: isEmpty(formState.current_salary),
        message: "今の年収は入力必須です。",
      },
      {
        field: "current_salary",
        isError:
          !isEmpty(formState.current_salary) &&
          isEmptyOrNegative(formState.current_salary),
        message: "今の年収は0以上を入力してください。",
      },
      {
        field: "career_change_count",
        isError: isEmpty(formState.career_change_count),
        message: "次で何回目の転職かは入力必須です。",
      },
      {
        field: "career_change_count",
        isError:
          !isEmpty(formState.career_change_count) &&
          isEmptyOrNegative(formState.career_change_count),
        message: "次で何回目の転職かは0以上を入力してください。",
      },
      {
        field: "previous_companies_and_reasons",
        isError: isEmpty(formState.previous_companies_and_reasons),
        message: "これまでの会社と退職理由は入力必須です。",
      },
      {
        field: "current_change_reason",
        isError: isEmpty(formState.current_change_reason),
        message: "今回の転職理由は入力必須です。",
      },
    ];

    rules.forEach((rule) => {
      if (rule.isError) {
        newErrors.push(rule.message);
      }
    });

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleChange = (e) => {
    setValidationTriggered(false);
    const { name, value } = e.target;
    setFormState({ ...formState, [name]: value });
  };

  // フォーム送信前にバリデーションチェック
  const handleSubmit = (e) => {
    e.preventDefault();
    setValidationTriggered(true);
    if (validateForm()) {
      document.getElementById("user-job-request-form").submit();
    }
  };

  const remarkCharacterCount = formState.remark ? formState.remark.length : 0;
  const previousCompaniesCharacterCount =
    formState.previous_companies_and_reasons
      ? formState.previous_companies_and_reasons.length
      : 0;
  const currentChangeReasonCharacterCount = formState.current_change_reason
    ? formState.current_change_reason.length
    : 0;

  return (
    <Container maxWidth="md" sx={{ position: "relative", mt: 2, mb: 10 }}>
      <Button
        onClick={() => window.history.back()}
        size="small"
        sx={{
          mb: 0.5,
          color: "#000",
          fontWeight: "bold",
          textTransform: "none",
          "&:hover": { opacity: 0.7 },
        }}
      >
        ← 戻る
      </Button>

      <form
        id="user-job-request-form"
        action={formAction}
        method="post"
        onSubmit={handleSubmit}
      >
        <input type="hidden" name="_method" value="patch" />
        <input type="hidden" name="authenticity_token" value={csrfToken} />

        {/* 希望勤務地（prefecture_ids）の送信は複数のhidden inputで対応 */}
        {formState.prefecture_ids.map((id, index) => (
          <input
            key={`prefecture-${index}`}
            type="hidden"
            name="user_job_request[prefecture_ids][]"
            value={id}
          />
        ))}

        {/* その他のformState値はhidden inputで一括送信 */}
        {Object.entries(formState).map(([key, value]) => {
          if (key === "prefecture_ids") return null;
          return (
            <input
              key={key}
              type="hidden"
              name={`user_job_request[${key}]`}
              value={value ?? ""}
            />
          );
        })}

        {/* エラーの表示 */}
        <Box sx={{ bgcolor: "#fff", p: 4, borderRadius: 2, boxShadow: 1 }}>
          {validationTriggered && errors.length > 0 && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errors.map((error, idx) => (
                <div key={idx}>{error}</div>
              ))}
            </Alert>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              転職先の募集要項
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                mt: 1,
                display: "block",
                borderBottom: "1px solid #ccc",
                pb: 1,
              }}
            >
              更新日時：{formatJpDateTime(userJobRequest?.updated_at)}
            </Typography>
          </Box>

          <Grid container spacing={1}>
            <Grid item xs={12} sm={6} sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: 14, mb: 1 }}>入社希望日</Typography>
              <TextField
                fullWidth
                name="desired_joining_date"
                type="date"
                value={formState.desired_joining_date}
                onChange={handleChange}
                error={isInvalidJoiningDate}
              />
            </Grid>

            <Grid item xs={12} sm={6} sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: 14, mb: 1 }}>退社予定日</Typography>
              <TextField
                fullWidth
                name="expected_retirement_date"
                type="date"
                value={formState.expected_retirement_date}
                onChange={handleChange}
                error={isInvalidRetirementDate}
              />
            </Grid>

            <Grid item xs={12} sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: 14, mb: 1 }}>
                (額面)希望年収
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={5.5}>
                  <TextField
                    fullWidth
                    name="expected_salary_min"
                    type="number"
                    value={formState.expected_salary_min}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val.length <= 6) handleChange(e);
                    }}
                    placeholder="300"
                    error={isInvalidSalaryMin}
                    sx={{
                      "& .MuiOutlinedInput-root input": {
                        boxShadow: "none",
                        outline: "none",
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">万円</InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={1} sx={{ textAlign: "center" }}>
                  <Typography sx={{ mx: 1 }}>〜</Typography>
                </Grid>

                <Grid item xs={12} sm={5.5}>
                  <TextField
                    fullWidth
                    name="expected_salary_max"
                    type="number"
                    value={formState.expected_salary_max}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val.length <= 6) handleChange(e);
                    }}
                    placeholder="500"
                    error={isInvalidSalaryMax}
                    sx={{
                      "& .MuiOutlinedInput-root input": {
                        boxShadow: "none",
                        outline: "none",
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">万円</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* 都道府県（共通コンポーネント） */}
            <Grid item xs={12} sx={{ mb: 1 }}>
              <PrefectureSelect
                value={formState.prefecture_ids}
                onChange={(ids) =>
                  setFormState((prev) => ({ ...prev, prefecture_ids: ids }))
                }
                label="希望勤務地"
                max={3}
                isRequired={true}
                error={isInvalidPrefecture}
              />
            </Grid>

            <Grid item xs={12} sx={{ mb: 2 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Typography sx={{ fontSize: 14 }}>希望雇用形態</Typography>
                <RequiredLabel show={true} />
              </Box>
              <TextField
                select
                fullWidth
                name="desired_employment_type"
                value={formState.desired_employment_type}
                onChange={handleChange}
                error={isInvalidEmploymentType}
              >
                <MenuItem value="full_time">正社員</MenuItem>
                <MenuItem value="contract_employee">契約社員</MenuItem>
                <MenuItem value="contractor">業務委託</MenuItem>
                <MenuItem value="temporary">派遣社員</MenuItem>
                <MenuItem value="part_time">アルバイト・パート</MenuItem>
                <MenuItem value="other">その他</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Typography sx={{ fontSize: 14, mb: 1 }}>
                備考(最大200文字まで)
              </Typography>
              <TextField
                fullWidth
                name="remark"
                value={formState.remark}
                onChange={handleChange}
                multiline
                rows={4}
                placeholder="補足事項や特記事項をご記入ください"
                sx={{ "& textarea:focus": { boxShadow: "none" } }}
                helperText={`${remarkCharacterCount}文字`}
                inputProps={{ maxLength: 200 }}
                FormHelperTextProps={{
                  sx: {
                    textAlign: "right",
                    marginRight: "0",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} sx={{ mb: 2 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Typography sx={{ fontSize: 14 }}>(額面)今の年収</Typography>
                <RequiredLabel show={true} />
              </Box>
              <TextField
                fullWidth
                name="current_salary"
                type="number"
                value={formState.current_salary}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.length <= 6) handleChange(e);
                }}
                placeholder="350"
                error={isInvalidCurrentSalary}
                sx={{
                  "& .MuiOutlinedInput-root input": {
                    boxShadow: "none",
                    outline: "none",
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">万円</InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} sx={{ mb: 2 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Typography sx={{ fontSize: 14 }}>
                  次で何回目の転職か
                </Typography>
                <RequiredLabel show={true} />
              </Box>
              <TextField
                fullWidth
                name="career_change_count"
                type="number"
                value={formState.career_change_count}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.length <= 3) handleChange(e);
                }}
                placeholder="2"
                error={isInvalidChangeCount}
                sx={{
                  "& .MuiOutlinedInput-root input": {
                    boxShadow: "none",
                    outline: "none",
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">回</InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Typography
                  sx={{
                    fontSize: 14,
                    whiteSpace: "pre-line",
                    lineHeight: 1.6,
                    display: "block",
                  }}
                >
                  これまでの会社と退職理由
                  <Box
                    component="br"
                    sx={{ display: { xs: "block", sm: "none" } }}
                  />
                  (最大200文字まで)
                </Typography>
                <RequiredLabel show={true} />
              </Box>
              <TextField
                fullWidth
                name="previous_companies_and_reasons"
                value={formState.previous_companies_and_reasons}
                onChange={handleChange}
                multiline
                rows={4}
                placeholder="①株式会社〇〇　退職理由：評価制度"
                error={isInvalidPreviousReasons}
                sx={{ "& textarea:focus": { boxShadow: "none" } }}
                helperText={`${previousCompaniesCharacterCount}文字`}
                inputProps={{ maxLength: 200 }}
                FormHelperTextProps={{
                  sx: {
                    textAlign: "right",
                    marginRight: "0",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Typography sx={{ fontSize: 14 }}>
                  今回の転職理由(最大50文字まで)
                </Typography>
                <RequiredLabel show={true} />
              </Box>
              <TextField
                fullWidth
                name="current_change_reason"
                value={formState.current_change_reason}
                onChange={handleChange}
                placeholder="新しい技術に携わりたい。"
                error={isInvalidCurrentReason}
                helperText={`${currentChangeReasonCharacterCount}文字`}
                inputProps={{ maxLength: 50 }}
                FormHelperTextProps={{
                  sx: {
                    textAlign: "right",
                    marginRight: "0",
                  },
                }}
              />
            </Grid>
          </Grid>
          <Box
            sx={{
              position: "absolute",
              bottom: -60,
              right: 16,
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => (window.location.href = "/user_job_request")}
              sx={{
                backgroundColor: "#ffffff",
                color: "#000",
                borderRadius: 9999,
                px: 4,
                fontWeight: "bold",
                border: "1px solid #9ca3af",
                "&:hover": {
                  opacity: 0.9,
                  backgroundColor: "#f3f4f6",
                  borderColor: "#9ca3af",
                },
              }}
            >
              キャンセル
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="inherit"
              sx={{
                borderRadius: 4,
                color: "#fff",
                bgcolor: "#000",
                "&:hover": { bgcolor: "#333" },
              }}
            >
              更新
            </Button>
          </Box>
        </Box>
      </form>
    </Container>
  );
};

export default JobRequestEdit;
